// Self-contained E-IMZO connector — no external SDK files required.
//
// Talks directly to the E-IMZO desktop app's local websocket (CAPIWS protocol),
// verified against the official transport (npm "e-imzo" Capiws):
//   endpoint:  wss://127.0.0.1:64443/service/cryptapi  (HTTPS pages)
//              ws://127.0.0.1:64646/service/cryptapi    (HTTP pages)
//   message:   JSON.stringify({ plugin, name, arguments })
//   one socket per call (open → send → one message → close); a keyId from
//   load_key stays valid across calls (agent-side handle), so per-call is fine.
//
// Requires the E-IMZO desktop app running. On HTTPS the secure socket (64443)
// must be reachable (E-IMZO's localhost cert). Some deployments require the
// calling domain to be authorized — call setApiKeys([domain, key, ...]) first.

function wsUrl() {
	const secure = typeof window !== 'undefined' && window.location.protocol === 'https:'
	return (secure ? 'wss://127.0.0.1:64443' : 'ws://127.0.0.1:64646') + '/service/cryptapi'
}

// Open a socket, send one funcDef, resolve with the parsed response, close.
function call(funcDef, timeoutMs = 15000) {
	return new Promise((resolve, reject) => {
		let s
		try {
			s = new WebSocket(wsUrl())
		} catch (e) {
			return reject(new Error('EIMZO_WS_OPEN_FAILED'))
		}
		const timer = setTimeout(() => {
			try { s.close() } catch (e) { /* ignore */ }
			reject(new Error('EIMZO_WS_TIMEOUT'))
		}, timeoutMs)
		s.onopen = () => {
			try { s.send(JSON.stringify(funcDef)) }
			catch (e) { clearTimeout(timer); reject(e) }
		}
		s.onmessage = (event) => {
			clearTimeout(timer)
			let data = null
			try { data = JSON.parse(event.data) } catch (e) { /* leave null */ }
			try { s.close() } catch (e) { /* ignore */ }
			resolve(data)
		}
		s.onerror = () => { clearTimeout(timer); reject(new Error('EIMZO_WS_ERROR')) }
	})
}

function ensureSuccess(data) {
	if (!data || data.success === false) {
		throw new Error((data && (data.reason || data.message)) || 'EIMZO_CALL_FAILED')
	}
	return data
}

export function isEimzoAvailable() {
	return typeof window !== 'undefined' && typeof window.WebSocket !== 'undefined'
}

// Optional domain API keys (flat array: [domain, key, domain, key, ...]).
let API_KEYS = []
export function setApiKeys(arr) { API_KEYS = arr || [] }

export async function installApiKeys() {
	if (!API_KEYS.length) return true // many setups (localhost / older) don't require it
	ensureSuccess(await call({ name: 'apikey', arguments: API_KEYS }))
	return true
}

function parseAlias(alias) {
	const out = {}
	if (!alias) return out
	String(alias).split(',').forEach(pair => {
		const idx = pair.indexOf('=')
		if (idx > 0) out[pair.slice(0, idx).trim().toLowerCase()] = pair.slice(idx + 1).trim()
	})
	return out
}

function mapCert(c, i) {
	// Merge the cert item's own (structured) string props with the parsed alias —
	// E-IMZO may expose subject fields either way; we want all of them.
	const fields = {}
	for (const k in c) {
		if (Object.prototype.hasOwnProperty.call(c, k) && typeof c[k] !== 'object') {
			fields[k.toLowerCase()] = c[k]
		}
	}
	Object.assign(fields, parseAlias(c.alias))
	return {
		_id: 'cert-' + i,
		disk: c.disk,
		path: c.path,
		name: c.name,
		alias: c.alias,
		serialNumber: c.serialNumber || fields.serialnumber,
		_fields: fields,
	}
}

// Try the modern call first, then the alternate name, then per-disk enumeration.
async function tryList(funcDef) {
	let data
	try { data = await call(funcDef) } catch (e) { return null }
	if (data && data.success === true) return data.certificates || data.keys || []
	return null
}

async function listViaDisks() {
	const disksData = ensureSuccess(await call({ plugin: 'pfx', name: 'list_disks' }))
	const disks = disksData.disks || []
	let all = []
	for (const disk of disks) {
		const cd = ensureSuccess(await call({ plugin: 'pfx', name: 'list_certificates', arguments: [disk] }))
		all = all.concat((cd.certificates || []).map(c => ({ ...c, disk: c.disk || disk })))
	}
	return all
}

// Returns certificate objects (pass one back to loadKey()).
export async function listCertificates() {
	let arr = await tryList({ plugin: 'pfx', name: 'list_all_keys' })
	if (arr === null) arr = await tryList({ plugin: 'pfx', name: 'list_all_certificates' })
	if (arr === null) arr = await listViaDisks()
	return (arr || []).map(mapCert)
}

export async function loadKey(cert) {
	// Longer timeout: E-IMZO shows a PIN dialog here.
	const data = ensureSuccess(await call({
		plugin: 'pfx', name: 'load_key',
		arguments: [cert.disk, cert.path, cert.name, cert.alias],
	}, 120000))
	return data.keyId || data.keyID
}

export async function createPkcs7(keyId, dataStr) {
	const base64 = window.btoa(unescape(encodeURIComponent(dataStr)))
	const data = ensureSuccess(await call({
		plugin: 'pkcs7', name: 'create_pkcs7',
		arguments: [base64, keyId, 'no'],
	}, 120000))
	return { pkcs7: data.pkcs7_64, signatureHex: data.signature_hex }
}

// INN = 9-digit tax id. Official rule: TIN = INN || UID. The alias carries INN as
// `1.2.860.3.16.1.1` (legal entity) or `uid` (individual). Fallback: any 9-digit field.
export function certTin(cert) {
	const f = (cert && cert._fields) || {}
	const explicit = f['1.2.860.3.16.1.1'] || f.inn || f.uid || f.tin
	if (explicit) return String(explicit)
	for (const k in f) {
		if (/^\d{9}$/.test(String(f[k] || ''))) return String(f[k])
	}
	return ''
}

export function certPinfl(cert) {
	const f = (cert && cert._fields) || {}
	const explicit = f['1.2.860.3.16.1.2'] || f.pinfl
	if (explicit) return String(explicit)
	for (const k in f) {
		if (/^\d{14}$/.test(String(f[k] || ''))) return String(f[k])
	}
	return ''
}

// F.I.Sh. — the certificate holder (person), from CN.
export function certName(cert) {
	const f = (cert && cert._fields) || {}
	return f.cn || f.name || (cert && cert.name) || ''
}

// Organization (Tashkilot) — only present for legal-entity certificates.
export function certOrg(cert) {
	const f = (cert && cert._fields) || {}
	return f.o || f.organization || ''
}

// JISMONIY SHAXS (individual) vs YURIDIK SHAXS (legal entity).
// Determined by presence of an organization (O), NOT the `t` field (that's the job title).
export function certType(cert) {
	return certOrg(cert) ? 'YURIDIK SHAXS' : 'JISMONIY SHAXS'
}

export function certSerial(cert) {
	const f = (cert && cert._fields) || {}
	return (cert && cert.serialNumber) || f.serialnumber || ''
}

export function certValidFrom(cert) {
	const f = (cert && cert._fields) || {}
	return f.validfrom || f.valid_from || ''
}

export function certValidTo(cert) {
	const f = (cert && cert._fields) || {}
	return f.validto || f.valid_to || ''
}
